import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import BottomTabNavigation from '../../components/navigation/BottomTabNavigation';
import UserProfileHeader from '../../components/navigation/UserProfileHeader';
import { useAuth } from '../../contexts/AuthContext';
import { hasFeatureAccess, OFFICER_FEATURES } from '../../utils/rolePermissions';
import { supabase } from '../../lib/supabase';
import { logger } from '../../utils/logger';
import { format, subHours } from 'date-fns';
import ErrorRateCard from './components/ErrorRateCard';
import FailedTransactionsPanel from './components/FailedTransactionsPanel';
import SignupCompletionPanel from './components/SignupCompletionPanel';
import CriticalIssuesPanel from './components/CriticalIssuesPanel';
import ErrorTrendChart from './components/ErrorTrendChart';
import FilterControls from './components/FilterControls';
import { platformEventsService } from '../../services/platformEventsService';

const AdminErrorMonitoring = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('24h');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshCountdown, setRefreshCountdown] = useState(30);
  
  // Error metrics
  const [errorMetrics, setErrorMetrics] = useState({
    totalErrors: 0,
    errorRate: 0,
    lockManagerErrors: 0,
    rlsErrors: 0,
    validationErrors: 0,
    authErrors: 0,
    trend: 0
  });
  
  // Transaction metrics
  const [transactionMetrics, setTransactionMetrics] = useState({
    failedPayments: 0,
    failedSignups: 0,
    failedCheckIns: 0,
    totalFailures: 0,
    trend: 0
  });
  
  // Signup metrics
  const [signupMetrics, setSignupMetrics] = useState({
    totalSignups: 0,
    completedSignups: 0,
    pendingVerifications: 0,
    completionRate: 0,
    trend: 0
  });
  
  // Critical issues
  const [criticalIssues, setCriticalIssues] = useState([]);
  const [unacknowledgedCount, setUnacknowledgedCount] = useState(0);
  
  // Error trend data for chart
  const [errorTrendData, setErrorTrendData] = useState([]);
  
  // Real-time channel
  const [realtimeChannel, setRealtimeChannel] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Check officer access
  const hasAccess = hasFeatureAccess(userProfile, OFFICER_FEATURES?.MASTER_DASHBOARD, user);

  // Redirect non-officers
  useEffect(() => {
    if (!loading && userProfile && !hasAccess) {
      navigate('/settings-and-privacy');
    }
  }, [hasAccess, loading, userProfile, navigate]);

  // Load data on mount and when filters change
  useEffect(() => {
    loadAllData();
  }, [timeRange, severityFilter]);

  // Auto-refresh countdown
  useEffect(() => {
    if (autoRefresh && refreshCountdown > 0) {
      const timer = setTimeout(() => {
        setRefreshCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (autoRefresh && refreshCountdown === 0) {
      loadAllData();
      setRefreshCountdown(30);
    }
  }, [refreshCountdown, autoRefresh]);

  // Real-time subscription for critical events
  useEffect(() => {
    if (!hasAccess || !userProfile) return;

    const channel = platformEventsService?.subscribeToEvents(
      (event) => {
        // Update metrics in real-time
        if (event?.eventSeverity === 'critical') {
          setCriticalIssues(prev => [event, ...prev]?.slice(0, 20));
          setUnacknowledgedCount(prev => prev + 1);
          
          // Show browser notification
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('Critical Error Detected', {
              body: `${event?.eventType}: ${event?.eventCategory}`,
              icon: '/favicon.ico'
            });
          }
        }
        
        // Refresh metrics
        loadErrorMetrics();
      },
      {
        eventTypes: ['PAYMENT_FAILURE', 'SYSTEM_ANOMALY', 'USER_SIGNUP'],
        severity: severityFilter === 'all' ? undefined : [severityFilter]
      }
    );

    setRealtimeChannel(channel);

    return () => {
      if (channel) {
        platformEventsService?.unsubscribe(channel);
      }
    };
  }, [hasAccess, userProfile, severityFilter]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadErrorMetrics(),
        loadTransactionMetrics(),
        loadSignupMetrics(),
        loadCriticalIssues(),
        loadErrorTrendData()
      ]);
      setLastUpdated(new Date());
    } catch (error) {
      logger?.error('Failed to load admin error monitoring data', error);
    } finally {
      setLoading(false);
    }
  };

  const loadErrorMetrics = async () => {
    try {
      const hoursAgo = getHoursFromTimeRange(timeRange);
      const startTime = subHours(new Date(), hoursAgo)?.toISOString();

      // Get platform events with error severity
      const { data: errorEvents, error } = await supabase?.from('platform_events')?.select('*')?.gte('created_at', startTime)?.in('event_severity', severityFilter === 'all' ? ['warning', 'critical'] : [severityFilter]);

      if (error) throw error;

      // Categorize errors
      const lockManagerErrors = errorEvents?.filter(e => 
        e?.event_data?.error?.includes('LockManager') || 
        e?.event_data?.error?.includes('lock timeout')
      )?.length || 0;

      const rlsErrors = errorEvents?.filter(e => 
        e?.event_data?.error?.includes('RLS') || 
        e?.event_data?.error?.includes('permission denied') ||
        e?.event_data?.error?.includes('policy')
      )?.length || 0;

      const validationErrors = errorEvents?.filter(e => 
        e?.event_data?.error?.includes('validation') || 
        e?.event_data?.error?.includes('invalid data')
      )?.length || 0;

      const authErrors = errorEvents?.filter(e => 
        e?.event_type === 'USER_SIGNUP' && e?.event_severity === 'critical'
      )?.length || 0;

      const totalErrors = errorEvents?.length || 0;

      // Calculate error rate (errors per hour)
      const errorRate = (totalErrors / hoursAgo)?.toFixed(2);

      // Get previous period for trend
      const prevStartTime = subHours(new Date(), hoursAgo * 2)?.toISOString();
      const prevEndTime = startTime;
      
      const { data: prevErrors } = await supabase?.from('platform_events')?.select('*', { count: 'exact', head: true })?.gte('created_at', prevStartTime)?.lt('created_at', prevEndTime)?.in('event_severity', severityFilter === 'all' ? ['warning', 'critical'] : [severityFilter]);

      const prevCount = prevErrors?.length || 0;
      const trend = prevCount > 0 ? (((totalErrors - prevCount) / prevCount) * 100)?.toFixed(1) : 0;

      setErrorMetrics({
        totalErrors,
        errorRate: parseFloat(errorRate),
        lockManagerErrors,
        rlsErrors,
        validationErrors,
        authErrors,
        trend: parseFloat(trend)
      });
    } catch (error) {
      logger?.error('Failed to load error metrics', error);
    }
  };

  const loadTransactionMetrics = async () => {
    try {
      const hoursAgo = getHoursFromTimeRange(timeRange);
      const startTime = subHours(new Date(), hoursAgo)?.toISOString();

      // Failed payments
      const { data: failedPayments, error: paymentError } = await supabase?.from('payment_failures')?.select('*', { count: 'exact', head: true })?.gte('created_at', startTime);

      if (paymentError) throw paymentError;

      // Failed signups (platform events with USER_SIGNUP + critical)
      const { data: failedSignups, error: signupError } = await supabase?.from('platform_events')?.select('*', { count: 'exact', head: true })?.eq('event_type', 'USER_SIGNUP')?.eq('event_severity', 'critical')?.gte('created_at', startTime);

      if (signupError) throw signupError;

      // Failed check-ins (audit logs with failed status)
      const { data: failedCheckIns, error: checkInError } = await supabase?.from('audit_logs')?.select('*', { count: 'exact', head: true })?.eq('action_type', 'CREATE')?.ilike('details', '%symptom_entry%')?.ilike('details', '%error%')?.gte('created_at', startTime);

      if (checkInError) throw checkInError;

      const failedPaymentsCount = failedPayments || 0;
      const failedSignupsCount = failedSignups || 0;
      const failedCheckInsCount = failedCheckIns || 0;
      const totalFailures = failedPaymentsCount + failedSignupsCount + failedCheckInsCount;

      // Calculate trend
      const prevStartTime = subHours(new Date(), hoursAgo * 2)?.toISOString();
      const prevEndTime = startTime;
      
      const { count: prevFailures } = await supabase?.from('payment_failures')?.select('*', { count: 'exact', head: true })?.gte('created_at', prevStartTime)?.lt('created_at', prevEndTime);

      const trend = prevFailures > 0 ? (((totalFailures - prevFailures) / prevFailures) * 100)?.toFixed(1) : 0;

      setTransactionMetrics({
        failedPayments: failedPaymentsCount,
        failedSignups: failedSignupsCount,
        failedCheckIns: failedCheckInsCount,
        totalFailures,
        trend: parseFloat(trend)
      });
    } catch (error) {
      logger?.error('Failed to load transaction metrics', error);
    }
  };

  const loadSignupMetrics = async () => {
    try {
      const hoursAgo = getHoursFromTimeRange(timeRange);
      const startTime = subHours(new Date(), hoursAgo)?.toISOString();

      // Total signups (platform events with USER_SIGNUP)
      const { data: totalSignups, error: totalError } = await supabase?.from('platform_events')?.select('*')?.eq('event_type', 'USER_SIGNUP')?.gte('created_at', startTime);

      if (totalError) throw totalError;

      // Completed signups (users with verified email)
      const { data: completedSignups, error: completedError } = await supabase?.from('user_profiles')?.select('id, created_at')?.gte('created_at', startTime);

      if (completedError) throw completedError;

      // Get auth users to check email verification
      const userIds = completedSignups?.map(u => u?.id) || [];
      let verifiedCount = 0;

      if (userIds?.length > 0) {
        // Count users with profiles (profile creation means email was verified)
        verifiedCount = completedSignups?.length || 0;
      }

      const totalSignupsCount = totalSignups?.length || 0;
      const completedSignupsCount = verifiedCount;
      const pendingVerifications = totalSignupsCount - completedSignupsCount;
      const completionRate = totalSignupsCount > 0 
        ? ((completedSignupsCount / totalSignupsCount) * 100)?.toFixed(1) 
        : 0;

      // Calculate trend
      const prevStartTime = subHours(new Date(), hoursAgo * 2)?.toISOString();
      const prevEndTime = startTime;
      
      const { data: prevSignups } = await supabase?.from('platform_events')?.select('*')?.eq('event_type', 'USER_SIGNUP')?.gte('created_at', prevStartTime)?.lt('created_at', prevEndTime);

      const prevCount = prevSignups?.length || 0;
      const trend = prevCount > 0 ? (((totalSignupsCount - prevCount) / prevCount) * 100)?.toFixed(1) : 0;

      setSignupMetrics({
        totalSignups: totalSignupsCount,
        completedSignups: completedSignupsCount,
        pendingVerifications,
        completionRate: parseFloat(completionRate),
        trend: parseFloat(trend)
      });
    } catch (error) {
      logger?.error('Failed to load signup metrics', error);
    }
  };

  const loadCriticalIssues = async () => {
    try {
      const hoursAgo = getHoursFromTimeRange(timeRange);
      const startTime = subHours(new Date(), hoursAgo)?.toISOString();

      const { data, error } = await supabase?.from('platform_events')?.select('*')?.eq('event_severity', 'critical')?.gte('created_at', startTime)?.order('created_at', { ascending: false })?.limit(20);

      if (error) throw error;

      // Convert to camelCase
      const issues = data?.map(issue => ({
        id: issue?.id,
        eventType: issue?.event_type,
        eventSeverity: issue?.event_severity,
        eventCategory: issue?.event_category,
        eventData: issue?.event_data,
        userId: issue?.user_id,
        acknowledged: issue?.acknowledged,
        createdAt: issue?.created_at
      })) || [];

      setCriticalIssues(issues);

      // Count unacknowledged
      const unacknowledged = issues?.filter(i => !i?.acknowledged)?.length;
      setUnacknowledgedCount(unacknowledged);
    } catch (error) {
      logger?.error('Failed to load critical issues', error);
    }
  };

  const loadErrorTrendData = async () => {
    try {
      const hoursAgo = getHoursFromTimeRange(timeRange);
      const startTime = subHours(new Date(), hoursAgo)?.toISOString();

      const { data, error } = await supabase?.from('platform_events')?.select('created_at, event_severity, event_type')?.gte('created_at', startTime)?.in('event_severity', ['warning', 'critical'])?.order('created_at', { ascending: true });

      if (error) throw error;

      // Group by hour
      const hourlyData = {};
      data?.forEach(event => {
        const hour = format(new Date(event.created_at), 'MMM dd HH:00');
        if (!hourlyData?.[hour]) {
          hourlyData[hour] = { time: hour, errors: 0, warnings: 0, critical: 0 };
        }
        hourlyData[hour].errors++;
        if (event?.event_severity === 'warning') hourlyData[hour].warnings++;
        if (event?.event_severity === 'critical') hourlyData[hour].critical++;
      });

      const trendData = Object.values(hourlyData);
      setErrorTrendData(trendData);
    } catch (error) {
      logger?.error('Failed to load error trend data', error);
    }
  };

  const getHoursFromTimeRange = (range) => {
    switch (range) {
      case '1h': return 1;
      case '6h': return 6;
      case '24h': return 24;
      case '7d': return 168;
      case '30d': return 720;
      default: return 24;
    }
  };

  const handleManualRefresh = () => {
    loadAllData();
    setRefreshCountdown(30);
  };

  const handleAcknowledgeIssue = async (issueId) => {
    try {
      const { error } = await supabase?.from('platform_events')?.update({
          acknowledged: true,
          acknowledged_by: user?.id,
          acknowledged_at: new Date()?.toISOString()
        })?.eq('id', issueId);

      if (error) throw error;

      // Update local state
      setCriticalIssues(prev => 
        prev?.map(issue => 
          issue?.id === issueId ? { ...issue, acknowledged: true } : issue
        )
      );
      setUnacknowledgedCount(prev => Math.max(0, prev - 1));

      logger?.audit('CRITICAL_ISSUE_ACKNOWLEDGED', user?.id, { issueId });
    } catch (error) {
      logger?.error('Failed to acknowledge issue', error);
    }
  };

  if (!hasAccess) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>Admin Error Monitoring - MyBodyToday</title>
        <meta name="description" content="Real-time error monitoring and critical issue tracking" />
      </Helmet>
      <div className="min-h-screen bg-background pb-20">
        <UserProfileHeader />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground flex items-center gap-3">
                  <Icon name="AlertTriangle" size={28} strokeWidth={2} className="text-red-600" />
                  Admin Error Monitoring
                </h1>
                <p className="text-muted-foreground mt-1">
                  Real-time error tracking and critical issue management
                </p>
              </div>
              
              <div className="flex items-center gap-3">
                {/* Auto-refresh toggle */}
                <button
                  onClick={() => setAutoRefresh(!autoRefresh)}
                  className={`px-3 py-2 rounded-lg border transition-colors ${
                    autoRefresh 
                      ? 'bg-green-50 border-green-200 text-green-700' :'bg-gray-50 border-gray-200 text-gray-600'
                  }`}
                >
                  <Icon name={autoRefresh ? 'Zap' : 'ZapOff'} size={18} strokeWidth={2} />
                </button>
                
                {/* Manual refresh */}
                <button
                  onClick={handleManualRefresh}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Icon name="RefreshCw" size={18} strokeWidth={2} />
                  Refresh
                </button>
              </div>
            </div>

            {/* Status bar */}
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  Live Monitoring Active
                </span>
                {lastUpdated && (
                  <span>
                    Last updated: {format(lastUpdated, 'HH:mm:ss')}
                  </span>
                )}
              </div>
              {autoRefresh && (
                <span>
                  Next refresh in {refreshCountdown}s
                </span>
              )}
            </div>
          </div>

          {/* Filter Controls */}
          <FilterControls
            timeRange={timeRange}
            setTimeRange={setTimeRange}
            severityFilter={severityFilter}
            setSeverityFilter={setSeverityFilter}
          />

          {/* Error Rate Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <ErrorRateCard
              title="Total Errors"
              value={errorMetrics?.totalErrors}
              rate={`${errorMetrics?.errorRate}/hr`}
              subtitle=""
              trend={errorMetrics?.trend}
              icon="AlertCircle"
              color="red"
            />
            <ErrorRateCard
              title="LockManager Timeouts"
              value={errorMetrics?.lockManagerErrors}
              subtitle="Auth lock errors"
              rate=""
              trend={0}
              icon="Lock"
              color="orange"
            />
            <ErrorRateCard
              title="RLS Permission Errors"
              value={errorMetrics?.rlsErrors}
              subtitle="Policy violations"
              rate=""
              trend={0}
              icon="Shield"
              color="yellow"
            />
            <ErrorRateCard
              title="Validation Errors"
              value={errorMetrics?.validationErrors}
              subtitle="Input validation"
              rate=""
              trend={0}
              icon="AlertTriangle"
              color="blue"
            />
          </div>

          {/* Error Trend Chart */}
          <ErrorTrendChart data={errorTrendData} loading={loading} />

          {/* Main Panels */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <FailedTransactionsPanel metrics={transactionMetrics} loading={loading} />
            <SignupCompletionPanel metrics={signupMetrics} loading={loading} />
          </div>

          {/* Critical Issues Panel */}
          <CriticalIssuesPanel
            issues={criticalIssues}
            unacknowledgedCount={unacknowledgedCount}
            onAcknowledge={handleAcknowledgeIssue}
            loading={loading}
          />
        </div>

        <BottomTabNavigation />
      </div>
    </>
  );
};

export default AdminErrorMonitoring;